import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { createLogger } from '../../../../utils/logger';
const logger = createLogger('DNRPreferencesInput');

import {
  FullDNRDetails,
  LimitedResuscitationDetails,
  SpecificMedicalSituationDetails,
  NaturalDeathDetails,
  DNRDetails,
  dnrPreferencesService
} from '../../../../services/dnrPreferencesInfo';

type DNRPreferencesInputScreenRouteProp = RouteProp<RootStackParamList, 'DNRPreferencesInput'>;

type DNRType = 'full_dnr' | 'limited' | 'specific' | 'natural' | null;
type LocationType = 'living_will' | 'living_trust' | 'advance_directive' | 'none' | null;

export const DNRPreferencesInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<DNRPreferencesInputScreenRouteProp>();

  const { dnrPreferencesData, isEditing } = route.params || {};

  const [selectedDNR, setSelectedDNR] = useState<DNRType>(
    dnrPreferencesData?.dnrType as DNRType || null
  );
  const [selectedLocation, setSelectedLocation] = useState<LocationType>(
    dnrPreferencesData?.locationOfDirective as LocationType || null
  );
  const [specialInstructions, setSpecialInstructions] = useState(
    dnrPreferencesData?.specialInstructions || ''
  );
  const [saving, setSaving] = useState(false);

  // Initialize details with existing data if editing
  const getInitialDetails = () => {
    if (dnrPreferencesData?.dnrDetails && dnrPreferencesData.dnrType) {
      return dnrPreferencesData.dnrDetails;
    }
    return null;
  };

  const initialDetails = getInitialDetails();

  // Full DNR Details
  const [fullDNRDetails, setFullDNRDetails] = useState<FullDNRDetails>({
    specificInterventionsToDecline: (initialDetails as FullDNRDetails)?.specificInterventionsToDecline || '',
    discussedWithHealthcareProvider: (initialDetails as FullDNRDetails)?.discussedWithHealthcareProvider || false,
    conditionsForCPR: (initialDetails as FullDNRDetails)?.conditionsForCPR || '',
    additionalInstructions: (initialDetails as FullDNRDetails)?.additionalInstructions || '',
  });

  // Limited Resuscitation Details
  const [limitedDetails, setLimitedDetails] = useState<LimitedResuscitationDetails>({
    acceptableInterventions: (initialDetails as LimitedResuscitationDetails)?.acceptableInterventions || '',
    limitedResuscitationConditions: (initialDetails as LimitedResuscitationDetails)?.limitedResuscitationConditions || '',
    medicationPreferences: (initialDetails as LimitedResuscitationDetails)?.medicationPreferences || '',
    timeLimits: (initialDetails as LimitedResuscitationDetails)?.timeLimits || '',
  });

  // Specific Medical Situation Details
  const [specificDetails, setSpecificDetails] = useState<SpecificMedicalSituationDetails>({
    triggeringConditions: (initialDetails as SpecificMedicalSituationDetails)?.triggeringConditions || '',
    exceptions: (initialDetails as SpecificMedicalSituationDetails)?.exceptions || '',
    treatmentPreferences: (initialDetails as SpecificMedicalSituationDetails)?.treatmentPreferences || '',
    discussedWithDoctor: (initialDetails as SpecificMedicalSituationDetails)?.discussedWithDoctor || false,
  });

  // Natural Death Details
  const [naturalDetails, setNaturalDetails] = useState<NaturalDeathDetails>({
    comfortCarePreferences: (initialDetails as NaturalDeathDetails)?.comfortCarePreferences || '',
    painManagementOptions: (initialDetails as NaturalDeathDetails)?.painManagementOptions || '',
    spiritualCulturalPractices: (initialDetails as NaturalDeathDetails)?.spiritualCulturalPractices || '',
    environmentPreferences: (initialDetails as NaturalDeathDetails)?.environmentPreferences || '',
  });

  const handleSave = async () => {
    // Validation - require at least DNR type selection
    if (!selectedDNR) {
      Alert.alert('Error', 'Please select a DNR preference type');
      return;
    }

    try {
      setSaving(true);

      // Determine which DNR details to save based on selected type
      let dnrDetails: DNRDetails = null;

      switch (selectedDNR) {
        case 'full_dnr':
          dnrDetails = fullDNRDetails;
          break;
        case 'limited':
          dnrDetails = limitedDetails;
          break;
        case 'specific':
          dnrDetails = specificDetails;
          break;
        case 'natural':
          dnrDetails = naturalDetails;
          break;
      }

      const dnrUpdateData = {
        dnrType: selectedDNR || '',
        locationOfDirective: selectedLocation || '',
        specialInstructions: specialInstructions,
        dnrDetails,
        notes: specialInstructions, // Map specialInstructions to notes field
      };

      let savedDNRPreferences;

      if (isEditing && dnrPreferencesData?.id) {
        // Update existing record
        savedDNRPreferences = await dnrPreferencesService.updateDNRPreferences(
          dnrPreferencesData.id,
          dnrUpdateData
        );
        // Handle wrapped response format { success, data, message }
        const updatedData = savedDNRPreferences?.data || savedDNRPreferences;
        logger.debug('DNR preferences updated successfully with ID:', updatedData.id);

        // Navigate to lock/unlock screen to allow re-selection
        navigation.navigate('DNRPreferencesLockUnlock', { dnrPreferencesId: updatedData.id });
      } else {
        // Create new record
        savedDNRPreferences = await dnrPreferencesService.createDNRPreferences(dnrUpdateData);
        // Handle wrapped response format { success, data, message }
        const createdData = savedDNRPreferences?.data || savedDNRPreferences;
        logger.debug('DNR preferences saved successfully with ID:', createdData.id);

        // Navigate to lock/unlock screen with the ID
        navigation.navigate('DNRPreferencesLockUnlock', { dnrPreferencesId: createdData.id });
      }
    } catch (error) {
      logger.error('Error saving DNR preferences:', error);
      Alert.alert('Error', 'Failed to save DNR preferences. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const renderDNRDetailsInput = () => {
    switch (selectedDNR) {
      case 'full_dnr':
        return (
          <View>
            <Text style={styles.detailsTitle}>Full DNR Details:</Text>

            {/* Specific interventions to decline */}
            <Text style={styles.inputLabel}>What specific medical interventions do you want to decline?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe specific interventions to decline..."
              value={fullDNRDetails.specificInterventionsToDecline}
              onChangeText={(value) =>
                setFullDNRDetails({ ...fullDNRDetails, specificInterventionsToDecline: value })
              }
              multiline
            />

            {/* Conditions for CPR */}
            <Text style={styles.inputLabel}>Are there any specific conditions under which you would want CPR?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe any exceptions or conditions..."
              value={fullDNRDetails.conditionsForCPR}
              onChangeText={(value) =>
                setFullDNRDetails({ ...fullDNRDetails, conditionsForCPR: value })
              }
              multiline
            />

            {/* Additional instructions */}
            <Text style={styles.inputLabel}>Any other specific instructions regarding no resuscitation?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Additional instructions..."
              value={fullDNRDetails.additionalInstructions}
              onChangeText={(value) =>
                setFullDNRDetails({ ...fullDNRDetails, additionalInstructions: value })
              }
              multiline
            />
          </View>
        );
      case 'limited':
        return (
          <View>
            <Text style={styles.detailsTitle}>Limited Resuscitation Details:</Text>

            {/* Acceptable interventions */}
            <Text style={styles.inputLabel}>What specific interventions would you accept?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe acceptable interventions..."
              value={limitedDetails.acceptableInterventions}
              onChangeText={(value) =>
                setLimitedDetails({ ...limitedDetails, acceptableInterventions: value })
              }
              multiline
            />

            {/* Conditions for limited resuscitation */}
            <Text style={styles.inputLabel}>Under what conditions would you want limited resuscitation?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe conditions for limited resuscitation..."
              value={limitedDetails.limitedResuscitationConditions}
              onChangeText={(value) =>
                setLimitedDetails({ ...limitedDetails, limitedResuscitationConditions: value })
              }
              multiline
            />

            {/* Medication preferences */}
            <Text style={styles.inputLabel}>What are your preferences for medication during resuscitation?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe medication preferences..."
              value={limitedDetails.medicationPreferences}
              onChangeText={(value) =>
                setLimitedDetails({ ...limitedDetails, medicationPreferences: value })
              }
              multiline
            />

            {/* Time limits */}
            <Text style={styles.inputLabel}>Any specific time limits for resuscitation attempts?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe time limits..."
              value={limitedDetails.timeLimits}
              onChangeText={(value) =>
                setLimitedDetails({ ...limitedDetails, timeLimits: value })
              }
              multiline
            />
          </View>
        );
      case 'specific':
        return (
          <View>
            <Text style={styles.detailsTitle}>Specific Medical Situations:</Text>

            {/* Triggering conditions */}
            <Text style={styles.inputLabel}>What specific medical conditions trigger your DNR?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe triggering conditions..."
              value={specificDetails.triggeringConditions}
              onChangeText={(value) =>
                setSpecificDetails({ ...specificDetails, triggeringConditions: value })
              }
              multiline
            />

            {/* Exceptions */}
            <Text style={styles.inputLabel}>Are there any exceptions to your DNR preferences?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe any exceptions..."
              value={specificDetails.exceptions}
              onChangeText={(value) =>
                setSpecificDetails({ ...specificDetails, exceptions: value })
              }
              multiline
            />

            {/* Treatment preferences */}
            <Text style={styles.inputLabel}>What treatments would you accept or decline?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe treatment preferences..."
              value={specificDetails.treatmentPreferences}
              onChangeText={(value) =>
                setSpecificDetails({ ...specificDetails, treatmentPreferences: value })
              }
              multiline
            />
          </View>
        );
      case 'natural':
        return (
          <View>
            <Text style={styles.detailsTitle}>Natural Death Preferences:</Text>

            {/* Comfort care preferences */}
            <Text style={styles.inputLabel}>What are your preferences for comfort care?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe comfort care preferences..."
              value={naturalDetails.comfortCarePreferences}
              onChangeText={(value) =>
                setNaturalDetails({ ...naturalDetails, comfortCarePreferences: value })
              }
              multiline
            />

            {/* Pain management options */}
            <Text style={styles.inputLabel}>What pain management options would you prefer?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe pain management preferences..."
              value={naturalDetails.painManagementOptions}
              onChangeText={(value) =>
                setNaturalDetails({ ...naturalDetails, painManagementOptions: value })
              }
              multiline
            />

            {/* Spiritual/cultural practices */}
            <Text style={styles.inputLabel}>Are there specific spiritual or cultural practices to consider?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe spiritual/cultural practices..."
              value={naturalDetails.spiritualCulturalPractices}
              onChangeText={(value) =>
                setNaturalDetails({ ...naturalDetails, spiritualCulturalPractices: value })
              }
              multiline
            />

            {/* Environment preferences */}
            <Text style={styles.inputLabel}>What environmental preferences do you have for end-of-life care?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe environmental preferences..."
              value={naturalDetails.environmentPreferences}
              onChangeText={(value) =>
                setNaturalDetails({ ...naturalDetails, environmentPreferences: value })
              }
              multiline
            />
          </View>
        );
      default:
        return null;
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>
          What is your Do Not Resuscitate Preference?
        </Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView style={styles.scrollView}>
          <View style={styles.radioGroup}>
            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => setSelectedDNR('full_dnr')}
            >
              <View style={styles.radioButton}>
                {selectedDNR === 'full_dnr' && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>Full DNR (No Resuscitation)</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => setSelectedDNR('limited')}
            >
              <View style={styles.radioButton}>
                {selectedDNR === 'limited' && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>Limited Resuscitation Preferences</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => setSelectedDNR('specific')}
            >
              <View style={styles.radioButton}>
                {selectedDNR === 'specific' && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>Specific Medical Situations</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => setSelectedDNR('natural')}
            >
              <View style={styles.radioButton}>
                {selectedDNR === 'natural' && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>Natural Death Preferences</Text>
            </TouchableOpacity>
          </View>

          {renderDNRDetailsInput()}

          <Text style={styles.label}>Do not resuscitate instructions:</Text>
          <TextInput
            style={styles.specialInstructionsInput}
            placeholder="Enter any special instructions or additional preferences regarding your DNR..."
            value={specialInstructions}
            onChangeText={setSpecialInstructions}
            multiline
          />

          <Text style={styles.sectionTitle}>
            Where is your Do Not Resuscitate Preference located?
          </Text>

          <View style={styles.radioGroup}>
            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => setSelectedLocation('living_will')}
            >
              <View style={styles.radioButton}>
                {selectedLocation === 'living_will' && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>In your living Will</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => setSelectedLocation('living_trust')}
            >
              <View style={styles.radioButton}>
                {selectedLocation === 'living_trust' && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>In your living Trust</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => setSelectedLocation('advance_directive')}
            >
              <View style={styles.radioButton}>
                {selectedLocation === 'advance_directive' && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>In your advanced directive</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => setSelectedLocation('none')}
            >
              <View style={styles.radioButton}>
                {selectedLocation === 'none' && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>None of the above</Text>
            </TouchableOpacity>
          </View>

          <TouchableOpacity
            style={[styles.saveButton, saving && { opacity: 0.7 }]}
            onPress={handleSave}
            disabled={saving}
          >
            {saving ? (
              <ActivityIndicator color="#FFFFFF" />
            ) : (
              <Text style={styles.saveButtonText}>{isEditing ? 'Update' : 'Save'}</Text>
            )}
          </TouchableOpacity>

          <View style={styles.bottomPadding} />
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(50),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
  backgroundKey: {
    position: 'absolute',
    width: '85%',
    height: '85%',
    opacity: 0.1,
  },
  logo: {
    width: s(125),
    height: s(125),
    marginBottom: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
    paddingHorizontal: ms(30),
    textAlign: 'center',
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
  },
  scrollView: {
    paddingHorizontal: ms(16),
  },
  radioGroup: {
    marginBottom: ms(20),
  },
  radioOption: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: ms(20),
  },
  radioButton: {
    width: s(24),
    height: s(24),
    borderRadius: ms(12),
    borderWidth: ms(2),
    borderColor: '#1C2C4F',
    marginRight: ms(10),
    justifyContent: 'center',
    alignItems: 'center',
  },
  radioButtonSelected: {
    width: s(12),
    height: s(12),
    borderRadius: ms(6),
    backgroundColor: '#1C2C4F',
  },
  radioText: {
    fontSize: ms(18, 0.3),
    color: '#1C2C4F',
  },
  detailsTitle: {
    fontSize: ms(16, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  detailsInput: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(10),
    padding: ms(15),
    marginBottom: ms(20),
    minHeight: s(100),
    textAlignVertical: 'top',
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
  },
  structuredInput: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(10),
    padding: ms(15),
    marginBottom: ms(15),
    minHeight: s(80),
    textAlignVertical: 'top',
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
  inputLabel: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(8),
    marginTop: ms(5),
  },
  sectionTitle: {
    fontSize: ms(20, 0.3),
    fontWeight: 'bold',
    color: '#F2994A',
    marginBottom: ms(20),
  },
  label: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(10),
  },
  specialInstructionsInput: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(10),
    padding: ms(15),
    marginBottom: ms(30),
    minHeight: s(150),
    textAlignVertical: 'top',
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
  },
  saveButton: {
    backgroundColor: '#29BFC0',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
    marginBottom: ms(30),
  },
  saveButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  bottomPadding: {
    height: s(120),
  },
});